import type { CSSProperties } from 'react';
import { CircleAlert, CircleCheck, Info, Loader2, TriangleAlert } from 'lucide-react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

import { useTheme } from '@/hooks/useTheme';

/**
 * App-wide toasts (FE-ADR-065): Sonner, following the app's light/dark mode
 * and drawn from the design tokens (popover card, border, radius), so a toast
 * looks like the rest of the UI. Bottom-right keeps the header and search
 * clear; toasts stack and expand on hover (which also pauses them), and a
 * small close button lets one be dismissed early.
 */
export function Toaster(props: ToasterProps) {
  const { isDarkMode } = useTheme();
  return (
    <Sonner
      theme={isDarkMode ? 'dark' : 'light'}
      position="bottom-right"
      duration={5000}
      icons={{
        success: <CircleCheck className="size-4 text-emerald-500" />,
        error: <CircleAlert className="size-4 text-destructive" />,
        warning: <TriangleAlert className="size-4 text-amber-500" />,
        info: <Info className="size-4 text-sky-500" />,
        loading: <Loader2 className="size-4 animate-spin" />,
      }}
      style={
        {
          '--normal-bg': 'hsl(var(--popover))',
          '--normal-text': 'hsl(var(--popover-foreground))',
          '--normal-border': 'hsl(var(--border))',
          '--border-radius': 'var(--radius)',
        } as CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: 'text-sm shadow-lg',
          description: 'text-muted-foreground',
        },
      }}
      closeButton
      {...props}
    />
  );
}
